import { motion } from "framer-motion";
import { Flame, Droplet, Leaf, Utensils } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function DailyPicture() {
  return (
    <section
      id="features"
      className="py-24 md:py-32 bg-zoku-bg relative"
      data-testid="daily-picture-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section heading */}
        <motion.div {...reveal} className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zoku-orange mb-4">
            The daily picture
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-zoku-ink">
            Your whole day, <span className="font-editorial text-zoku-orange">at a glance.</span>
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-xl">
            Every meal, every sip, every step — logged and understood. No spreadsheets. No guilt.
            Just the truth in plain English.
          </p>
        </motion.div>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Large food image card */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.05 }}
            className="md:col-span-7 rounded-[2rem] overflow-hidden relative bg-white border border-stone-100 min-h-[360px] group"
            data-testid="bento-nutrition"
          >
            <img
              src="https://images.pexels.com/photos/6632286/pexels-photo-6632286.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Nutritious bowl"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zoku-ink/60 via-zoku-ink/10 to-transparent" />
            <div className="relative z-10 p-7 md:p-9 h-full flex flex-col justify-end text-white">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-90">
                <Utensils className="w-4 h-4" /> Calories & macros
              </div>
              <h3 className="mt-3 font-heading text-3xl md:text-4xl font-bold leading-tight max-w-md">
                See what fuels you, per ingredient.
              </h3>
              <p className="mt-2 text-white/80 max-w-md text-sm md:text-base">
                Snap a photo or scan a barcode — Zoku breaks down protein, carbs, fat, fibre and
                hidden nasties in one tap.
              </p>
              <div className="mt-5 inline-flex items-center gap-5 bg-white/95 backdrop-blur text-zoku-ink rounded-2xl px-5 py-3 self-start shadow-xl">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zoku-ink3">Today</div>
                  <div className="font-heading text-xl font-bold">1,640 kcal</div>
                </div>
                <div className="h-8 w-px bg-stone-200" />
                <div className="flex gap-3 text-xs">
                  <div className="text-center">
                    <div className="font-bold text-zoku-ink">68g</div>
                    <div className="text-zoku-ink3">P</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-zoku-ink">186g</div>
                    <div className="text-zoku-ink3">C</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-zoku-ink">54g</div>
                    <div className="text-zoku-ink3">F</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right column */}
          <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-5">
            {/* Water */}
            <motion.div
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.1 }}
              className="rounded-[2rem] p-7 bg-gradient-to-br from-sky-50 to-white border border-sky-100 relative overflow-hidden"
              data-testid="bento-water"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center">
                    <Droplet className="w-4 h-4 text-sky-600" />
                  </div>
                  <span className="font-semibold text-zoku-ink">Water intake</span>
                </div>
                <span className="text-xs text-zoku-ink3">Goal · 3 L</span>
              </div>
              <div className="mt-6 flex items-end gap-3">
                <div className="font-heading text-5xl md:text-6xl font-bold text-zoku-ink leading-none">
                  1.8
                </div>
                <div className="text-zoku-ink3 text-sm mb-2">L logged</div>
              </div>
              <div className="mt-5 grid grid-cols-8 gap-1.5">
                {[1, 1, 1, 1, 1, 0, 0, 0].map((f, i) => (
                  <div
                    key={i}
                    className={`h-6 rounded-md transition-colors ${
                      f ? "bg-sky-400" : "bg-sky-100"
                    }`}
                  />
                ))}
              </div>
              <p className="mt-4 text-xs text-zoku-ink3">
                3 more glasses to close today's ring.
              </p>
            </motion.div>

            {/* Clean ingredients */}
            <motion.div
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.15 }}
              className="rounded-[2rem] p-7 bg-white border border-stone-100 relative overflow-hidden"
              data-testid="bento-ingredients"
            >
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Leaf className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="font-semibold text-zoku-ink">Ingredient safety</span>
              </div>
              <div className="mt-5 space-y-2.5">
                {[
                  { n: "Almonds", s: "All clear", cls: "bg-emerald-100 text-emerald-700" },
                  { n: "Glucose syrup", s: "Check more", cls: "bg-amber-100 text-amber-700" },
                  { n: "Phenoxyethanol", s: "Skip it", cls: "bg-rose-100 text-rose-700" },
                ].map((i, k) => (
                  <div
                    key={k}
                    className="flex items-center justify-between bg-stone-50 rounded-xl px-3 py-2.5"
                  >
                    <span className="text-sm text-zoku-ink">{i.n}</span>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${i.cls}`}>
                      {i.s}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Meals row */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.2 }}
            className="md:col-span-12 rounded-[2rem] p-7 md:p-9 bg-white border border-stone-100 flex flex-col md:flex-row gap-7 md:items-center"
            data-testid="bento-meals"
          >
            <div className="md:w-1/3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zoku-orange">
                <Flame className="w-4 h-4" /> Meals today
              </div>
              <h3 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-zoku-ink">
                Effortless logging, smart suggestions.
              </h3>
              <p className="mt-2 text-sm text-zoku-ink2">
                Zoku remembers your favourites. Add a meal in two taps, adjust portions with a
                slider.
              </p>
            </div>
            <div className="md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { e: "🥚", n: "Scrambled eggs + toast", t: "Breakfast · 8:14 AM", k: 380 },
                { e: "🥗", n: "Chicken Caesar salad", t: "Lunch · 1:02 PM", k: 520 },
                { e: "🥜", n: "Kind Bar — Almond", t: "Snack · 3:45 PM", k: 200 },
                { e: "🍝", n: "Pasta Arrabbiata", t: "Dinner · 7:30 PM", k: 540 },
              ].map((m, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-zoku-bg hover:bg-zoku-orangeLight/40 transition-colors border border-stone-100"
                >
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-xl shadow-sm">
                    {m.e}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zoku-ink truncate">{m.n}</div>
                    <div className="text-xs text-zoku-ink3">{m.t}</div>
                  </div>
                  <div className="text-sm font-bold text-zoku-ink">{m.k}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
